// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Minimal line drawings for empty states: thin strokes in the muted text colour, with a single
 * accent detail, drawn inline in theme colours so they follow light and dark mode. Settings
 * sections use hand-drawn images instead (assets/settings). Decorative: hidden from screen readers.
 */
import { cn } from "@granite/ui";
import type { ReactNode } from "react";

import emptyNote from "../assets/panel-empty.png?no-inline";
import notepad from "../assets/pane-empty.png?no-inline";

/** A 96×64 drawing. Strokes are muted; elements marked with `A` take the accent colour. */
function Art({ children, className }: { children: ReactNode; className?: string | undefined }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 96 64"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn("h-16 w-auto shrink-0 text-fg-muted/70", className)}
    >
      {children}
    </svg>
  );
}

const A = { stroke: "var(--color-accent)" } as const;
const Afill = { fill: "var(--color-accent)", stroke: "none" } as const;

/** Two notes, one behind the other: no vaults or no open note. */
export function NotesArt({ className }: { className?: string | undefined }) {
  return (
    <Art className={className}>
      <path d="M30 14h26l8 8v32H30Z" opacity="0.45" transform="translate(-6 -4)" />
      <path d="M32 14h24l8 8v32H32Z" fill="var(--color-bg)" />
      <path d="M56 14v8h8" />
      <path d="M38 30h18M38 36h20M38 42h12" />
      <path d="M38 24h8" style={A} />
      <circle cx="70" cy="48" r="1.5" style={Afill} />
    </Art>
  );
}

/** A small group around one table: no workspaces yet. */
export function TeamArt({ className }: { className?: string | undefined }) {
  return (
    <Art className={className}>
      <circle cx="34" cy="24" r="5" />
      <circle cx="62" cy="24" r="5" />
      <circle cx="48" cy="20" r="5" style={A} />
      <path d="M26 40c0-5 4-8 8-8s8 3 8 8M54 40c0-5 4-8 8-8s8 3 8 8M40 36c0-5 4-8 8-8s8 3 8 8" />
      <path d="M22 46h52" />
    </Art>
  );
}

/** Each settings section's drawing, keyed by section id. */
// Separate files (never inlined into the script), fetched when a section opens and cached.
const sectionArt = import.meta.glob<string>("../assets/settings/*.png", { eager: true, import: "default", query: "?no-inline" });

/**
 * The drawing at the top of a settings section. The images are black shapes on transparency,
 * used as a mask over the muted text colour so they follow light and dark mode.
 */
export function SectionArt({ section, className }: { section: string; className?: string | undefined }) {
  const url = sectionArt[`../assets/settings/${section}.png`];
  if (!url) return null;
  const mask = `url("${url}") center / contain no-repeat`;
  return (
    <span
      aria-hidden="true"
      data-art={section}
      className={cn("block size-16 shrink-0 bg-fg-muted", className)}
      style={{ mask, WebkitMask: mask }}
    />
  );
}

/** A drawing (black on transparency) shown in the muted text colour, so it follows the theme. */
function Drawing({ src, className }: { src: string; className: string }) {
  const mask = `url("${src}") center / contain no-repeat`;
  return <span aria-hidden="true" className={cn("block shrink-0 bg-fg-muted/70", className)} style={{ mask, WebkitMask: mask }} />;
}

/** A notepad: no note is open yet. */
export function NotepadArt() {
  return <Drawing src={notepad} className="size-20" />;
}

/**
 * A side panel with nothing to show: a note drawing above the message, centred in the panel.
 * The image is black on transparency, used as a mask over the muted text colour.
 */
export function PanelEmpty({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 pb-2 text-center">
      <Drawing src={emptyNote} className="size-14" />
      <p className="max-w-60 text-sm text-fg-muted">{children}</p>
    </div>
  );
}
